<app-loading-overlay [isLoading]="uploading" class="dialog-fill">
  <bv-dialog-title>{{ clubServ.latestClub?.clubName }}</bv-dialog-title>

  <bv-dialog-body>
    <!-- Staff view -->
    @if (clubServ.clubStaff) {
      <form>
        <bv-form-field label="{{ 'CLUB_NAME' | translate }}" class="club-settings__name-field">
          <input
            bv-input
            type="text"
            [(ngModel)]="clubName"
            name="clubName"
            required
            />
        </bv-form-field>
        <div class="club-settings__images">
          <!-- Club Logo -->
          <div class="club-settings__image-col">
            <span class="club-settings__image-label">{{ "CLUB_LOGO" | translate }}</span>
            <div class="club-settings__image-frame">
              <img
                class="club-settings__image"
                [src]="clubLogoURL || '/assets/img/club-no-logo.png'"
                />
              @if (clubLogoURL) {
                <button
                  id="btn-delete-club-logo"
                  bv-button variant="ghost" iconOnly
                  class="club-settings__image-delete"
                  (click)="removeClubImage('CLUB_LOGO')"
                  >
                  <bv-icon name="close"></bv-icon>
                </button>
              }
            </div>
            <app-feature-lock feature="LOGO" tier="HOME_GAME" [subLevel]="20">
              <app-file-input
                [disabled]="!clubServ.minClubLevel(10)"
                (select)="logoChange($event)"
                accept="image/*"
                >
                <bv-icon name="upload"></bv-icon>
                <span>{{ "CHOOSE_A_FILE" | translate }}</span>
              </app-file-input>
            </app-feature-lock>
          </div>
          <!-- Clock Background -->
          <div class="club-settings__image-col">
            <span class="club-settings__image-label">{{ "CLOCK_BACKGROUND_IMAGE" | translate }}</span>
            <div class="club-settings__image-frame">
              <img
                class="club-settings__image"
                [src]="backURL || '/assets/img/no-image-box.png'"
                />
              @if (backURL) {
                <button
                  id="btn-delete-clock-background"
                  bv-button variant="ghost" iconOnly
                  class="club-settings__image-delete"
                  (click)="removeClubImage('CLOCK_BACKGROUND_IMAGE')"
                  >
                  <bv-icon name="close"></bv-icon>
                </button>
              }
            </div>
            <app-feature-lock feature="BACKGROUND" tier="HOME_GAME" [subLevel]="20">
              <app-file-input
                [disabled]="!clubServ.minClubLevel(10)"
                (select)="backChange($event)"
                accept="image/*"
                >
                <bv-icon name="upload"></bv-icon>
                <span>{{ "CHOOSE_A_FILE" | translate }}</span>
              </app-file-input>
            </app-feature-lock>
          </div>
        </div>
        <!-- Locations (venues). Rows open the editor on top of this dialog. -->
        <div class="club-settings__locations">
          <div class="club-settings__locations-header">
            <span class="club-settings__image-label">{{ "LOCATIONS" | translate }}</span>
            <span class="flex-1"></span>
            <app-feature-lock feature="LOCATIONS" tier="CLUB" [subLevel]="30">
              <button
                id="btn-new-location"
                bv-button variant="ghost" size="sm"
                type="button"
                (click)="addLocation()"
                >
                <bv-icon name="add"></bv-icon>
                {{ "NEW_LOCATION" | translate }}
              </button>
            </app-feature-lock>
          </div>
          @if (clubServ.locations().length) {
            <ul class="club-settings__location-list">
              @for (loc of clubServ.locations(); track loc.locationID) {
                <li>
                  <button
                    type="button"
                    class="club-settings__location-row"
                    (click)="editLocation(loc.locationID)"
                    >
                    <bv-icon name="place" size="sm" class="club-settings__location-icon"></bv-icon>
                    <span class="club-settings__location-label">{{ clubServ.locationLabel(loc, true) }}</span>
                    @if (isHomeLocation(loc)) {
                      <bv-badge variant="brand">{{ "HOME_LOCATION" | translate }}</bv-badge>
                    }
                    <bv-icon name="chevron_right" size="sm" class="club-settings__location-chevron"></bv-icon>
                  </button>
                </li>
              }
            </ul>
          } @else {
            <p class="club-settings__locations-empty">{{ "NO_LOCATIONS_DESC" | translate }}</p>
          }
        </div>
        <!-- Joining: the club's permanent link / QR poster + join policy -->
        <ng-container *ngTemplateOutlet="clubLink; context: { staff: true }"></ng-container>
        @if (!clubServ.minClubLevel(10)) {
          <div
            class="club-settings__upgrade-notice"
            >
            <a
              [routerLink]="'/subscription'"
              (click)="dialog.closeAll()"
            >{{ "FEATURE_REQUIRES_PAID_PLAN" | translate }}</a>
          </div>
        }
      </form>
    }

    <!-- Non-staff view -->
    @if (!clubServ.clubStaff) {
      <div class="club-settings__readonly">
        <div class="club-settings__readonly-row">
          <span class="club-settings__readonly-label">{{ "CLUB_NAME" | translate }}</span>
          <span class="club-settings__readonly-value">{{ clubName }}</span>
        </div>
        @if (clubServ.homeLocation()) {
          <div class="club-settings__readonly-row">
            <span class="club-settings__readonly-label">{{ "HOME_LOCATION" | translate }}</span>
            <span class="club-settings__readonly-value">{{ clubServ.locationLabel(clubServ.homeLocation(), true) }}</span>
          </div>
        }
        <div class="club-settings__readonly-row">
          <span class="club-settings__readonly-label">{{ "CLUB_LOGO" | translate }}</span>
          <div class="club-settings__image-frame club-settings__image-frame--small">
            <img
              class="club-settings__image"
              [src]="clubLogoURL || '/assets/img/club-no-logo.png'"
              />
          </div>
        </div>
        <ng-container *ngTemplateOutlet="clubLink; context: { staff: false }"></ng-container>
      </div>
    }

    <!-- Joining block, shared by the staff form (with policy) and the member view. -->
    <ng-template #clubLink let-staff="staff">
      <div class="club-settings__joining">
        <span class="club-settings__section-label">{{ "JOIN_POLICY" | translate }}</span>
        <app-club-join-link>
          @if (staff) {
            <bv-radio-group [(ngModel)]="joinPolicy" name="joinPolicy" orientation="responsive">
              <bv-radio value="approval">{{ "JOIN_POLICY_APPROVAL" | translate }}</bv-radio>
              <bv-radio value="open">{{ "JOIN_POLICY_OPEN" | translate }}</bv-radio>
            </bv-radio-group>
          }
        </app-club-join-link>
      </div>
    </ng-template>
  </bv-dialog-body>

  <!-- Actions -->
  <bv-dialog-footer>
    @if (clubServ.clubStaff) {
      <button
        id="btn-confirm-club-update"
        bv-button variant="primary"
        (click)="changeClub()"
        >
        {{ "CONFIRM" | translate }}
      </button>
    }
    @if (clubServ.clubOwner) {
      <button
        id="btn-delete-club"
        bv-button variant="ghost" class="tc-error"
        (click)="deleteClub()"
        [disabled]="profServ.disableDelete"
        >
        {{ "DELETE_CLUB" | translate }}
      </button>
    }
    <span class="flex-1"></span>
    @if (clubServ.clubStaff) {
      <app-close-dialog-button
        label="{{ 'CANCEL' | translate }}"
      ></app-close-dialog-button>
    }
    @if (!clubServ.clubStaff) {
      <app-close-dialog-button
      ></app-close-dialog-button>
    }
  </bv-dialog-footer>
</app-loading-overlay>
